import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { camelCase, replace } from "lodash";

import logo from "~/assets/images/logo-white.svg";
import transition from "~/utilities/transition";
import { screenReaderText } from "~/styles/screenReaderText";

import ContentContainer from "~/components/layout/ContentContainer";

import IconButton from "~/components/buttons/IconButton";

import BaseLink from "~/components/links/BaseLink";
import ImageLink from "~/components/links/ImageLink";
import SocialLinks from "~/components/links/SocialLinks";

import Form from "~/queryBlocks/Form";

import useGetBasePath from "~/hooks/useGetBasePath";
import useStringTranslation from "~/hooks/translations/useStringTranslation";

const Row = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin: -15px -15px 0;
    justify-content: space-between;
    min-width: 100%;
`;

const Col = styled.div`
    padding: 15px 15px 0;
`;

const Top = styled.div`
    background-color: ${({ theme }) => theme.palette.purple};
    padding: 20px 0;
`;

const Bottom = styled(ContentContainer)`
    padding-top: 20px;
    padding-bottom: 20px;
`;

const NavList = styled.ul`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin: -10px -15px 0;
`;

const NavListItem = styled.li`
    padding: 10px 10px 0;
`;

const NavLink = styled(BaseLink)`
    ${transition({}, "color")};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.typography.defaultLightBackground};

    &:hover,
    &:focus {
        text-decoration: underline;
    }
`;

const SecondaryNavLink = styled(BaseLink)`
    ${transition({}, "color")};
    text-transform: uppercase;
    font-size: 1.4rem;
    font-weight: 400;
    letter-spacing: 0.08em;
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.typography.defaultLightBackground};

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.colors.typography.links};
    }
`;

const Copyright = styled(ContentContainer)`
    text-transform: uppercase;
    text-align: center;
    font-size: 1.4rem;
    font-weight: 400;
    letter-spacing: 0.08em;
    padding-top: 20px;
    padding-bottom: 20px;
`;

const today = new Date();

const NewsletterCol = styled(Col)`
    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        margin-left: auto;
    }
`;

const HiddenFooterHeadingLabel = styled.h2`
    ${screenReaderText}
`;

const HiddenFooterSubLabel = styled.h3`
    ${screenReaderText}
`;

const Footer = ({
    socialLinks = [],
    navLinks = [],
    secondaryNavLinks = [],
    footerFormInfo = {},
    copyright = "",
}) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const {
        footerText,
        footerLinksText,
        footerNavigationText,
        socialMediaText,
        legalText,
    } = useStringTranslation([
        { text: "Footer", handle: "footerText" },
        { text: "Footer Links", handle: "footerLinksText" },
        { text: "Footer Navigation", handle: "footerNavigationText" },
        { text: "Social Media", handle: "socialMediaText" },
        { text: "Legal", handle: "legalText" },
    ]);
    const getBasePath = useGetBasePath();

    const homeUrl = getBasePath("pagesBase")?.url || "/";

    return (
        <footer>
            <HiddenFooterHeadingLabel id="footerLabel">
                {footerText}
            </HiddenFooterHeadingLabel>

            <Top>
                <ContentContainer>
                    <Row>
                        <Col>
                            <ImageLink
                                to={homeUrl}
                                image={{
                                    source: logo,
                                    alt: "Wolfspeed Logo",
                                    width: "140",
                                    height: "45",
                                }}
                            />
                        </Col>

                        {footerFormInfo.formId &&
                            footerFormInfo.buttonLabel && (
                                <NewsletterCol xs="auto">
                                    <Form
                                        formId={footerFormInfo.formId}
                                        modalForm={true}
                                        isModalOpenProp={modalIsOpen}
                                        closeModalProp={() =>
                                            setModalIsOpen(false)
                                        }
                                        useChildButton={true}
                                    >
                                        <IconButton
                                            color="whiteAlt"
                                            icon={footerFormInfo.buttonIcon}
                                            onClick={() => setModalIsOpen(true)}
                                        >
                                            {footerFormInfo.buttonLabel}
                                        </IconButton>
                                    </Form>
                                </NewsletterCol>
                            )}
                        <Col>
                            <HiddenFooterSubLabel id="socialMediaLabel">
                                {socialMediaText}
                            </HiddenFooterSubLabel>

                            {socialLinks.length > 0 && (
                                <NavList
                                    role="list"
                                    aria-labelledby="socialMediaLabel"
                                >
                                    <SocialLinks
                                        color="whiteAlt"
                                        links={socialLinks}
                                    />
                                </NavList>
                            )}
                        </Col>
                    </Row>
                </ContentContainer>
            </Top>

            {(navLinks.length > 0 || secondaryNavLinks.length > 0) && (
                <Bottom>
                    <nav aria-label={footerLinksText}>
                        <Row>
                            {navLinks.length > 0 && (
                                <Col>
                                    <HiddenFooterSubLabel id="footerNavigationLabel">
                                        {footerNavigationText}
                                    </HiddenFooterSubLabel>

                                    <NavList aria-labelledby="footerNavigationLabel">
                                        {navLinks.map(
                                            ({ linkLabel, linkUrl }) => {
                                                const key = `NavLink-${camelCase(
                                                    linkUrl,
                                                )}-${camelCase(linkLabel)}`;
                                                return (
                                                    <NavListItem key={key}>
                                                        <NavLink to={linkUrl}>
                                                            {linkLabel}
                                                        </NavLink>
                                                    </NavListItem>
                                                );
                                            },
                                        )}
                                    </NavList>
                                </Col>
                            )}
                            {secondaryNavLinks.length > 0 && (
                                <Col>
                                    <HiddenFooterSubLabel id="legalNavigationLabel">
                                        {legalText}
                                    </HiddenFooterSubLabel>

                                    <NavList aria-labelledby="legalNavigationLabel">
                                        {secondaryNavLinks.map(
                                            ({ linkLabel, linkUrl }, i) => (
                                                <NavListItem
                                                    key={`LegalLink-${linkLabel}-${i}`}
                                                >
                                                    <SecondaryNavLink
                                                        to={linkUrl}
                                                    >
                                                        {linkLabel}
                                                    </SecondaryNavLink>
                                                </NavListItem>
                                            ),
                                        )}
                                    </NavList>
                                </Col>
                            )}
                        </Row>
                    </nav>
                </Bottom>
            )}
            {copyright ? (
                <Copyright>
                    {replace(copyright, "{{year}}", today.getFullYear())}
                </Copyright>
            ) : (
                <Copyright>
                    {`Copyright © ${today.getFullYear()} Wolfspeed, Inc.`}
                </Copyright>
            )}
        </footer>
    );
};

Footer.propTypes = {
    copyright: PropTypes.string,
    socialLinks: PropTypes.arrayOf(
        PropTypes.shape({
            icon: PropTypes.string.isRequired,
            linkUrl: PropTypes.string.isRequired,
            screenReaderText: PropTypes.string.isRequired,
        }),
    ),
    navLinks: PropTypes.arrayOf(
        PropTypes.shape({
            linkLabel: PropTypes.string.isRequired,
            linkUrl: PropTypes.string.isRequired,
        }),
    ),
    secondaryNavLinks: PropTypes.arrayOf(
        PropTypes.shape({
            linkLabel: PropTypes.string.isRequired,
            linkUrl: PropTypes.string.isRequired,
        }),
    ),
    footerFormInfo: PropTypes.shape({
        buttonIcon: PropTypes.string,
        buttonLabel: PropTypes.string,
        formId: PropTypes.number,
    }),
};

export default Footer;
